/**
 * PERSONA 5 ROYAL CART OVERLAY MASTER MOTION ENGINE (GSAP 3 - AAA MOTION POLISHED)
 * Mechanics: Authentic P5R Save/Load Screen Animation Sequence
 */

(function (global) {
  'use strict';

  class Persona5CartMotion {
    constructor(overlay) {
      this.overlay = overlay;
      if (!this.overlay) return;

      this.backdrop = this.overlay.querySelector('.p5r-cart-backdrop');
      this.shadowDrift = this.overlay.querySelector('.p5r-cart-bg-shadow-drift');
      this.closeBtn = this.overlay.querySelector('.p5r-cart-close');
      this.filmstripBlock = this.overlay.querySelector('.p5r-cart-filmstrip-block');
      this.headerTitle = this.overlay.querySelector('.p5r-cart-header');
      this.cardStack = this.overlay.querySelector('#p5rCartStackContainer');
      this.dossierPanel = this.overlay.querySelector('.p5r-cart-dossier');
      this.graphicStamps = this.overlay.querySelectorAll('.p5r-cart-stamp');
      this.promptBar = this.overlay.querySelector('.p5r-cart-prompt-bar');

      this.timeline = null;
      this.splitTypes = [];
      this.mouseX = 0;
      this.mouseY = 0;
      this.targetX = 0;
      this.targetY = 0;
      this.parallaxBound = false;

      this.init();
    }

    init() {
      this.initSplitType();
      this.initParallax();
      this.bindCheckoutTransition();
      this.buildMasterTimeline();
    }

    initSplitType() {
      if (typeof SplitType === 'undefined') return;

      const targets = this.overlay.querySelectorAll('.p5r-splittype-target');
      targets.forEach(target => {
        try {
          const split = new SplitType(target, { types: 'words, chars' });
          this.splitTypes.push(split);
        } catch (err) {
          console.warn('SplitType initialization error:', err);
        }
      });
    }

    resetToInitialState() {
      if (typeof gsap === 'undefined') return;

      const stackItems = this.overlay.querySelectorAll('.p5r-cart-card, .p5r-cart-empty');

      gsap.set(this.backdrop, { opacity: 0, force3D: true });

      if (this.shadowDrift) {
        gsap.set(this.shadowDrift, { opacity: 0, scale: 0.7, rotate: -8, force3D: true });
      }

      if (this.closeBtn) {
        gsap.set(this.closeBtn, { opacity: 0, y: -30, rotate: 6, force3D: true });
      }

      if (this.filmstripBlock) {
        gsap.set(this.filmstripBlock, { xPercent: -120, force3D: true });
      }

      if (this.headerTitle) {
        gsap.set(this.headerTitle, { opacity: 0, x: -50, rotate: -8, force3D: true });
      }

      if (this.graphicStamps.length > 0) {
        gsap.set(this.graphicStamps, { opacity: 0, scale: 1.35, force3D: true });
      }

      if (stackItems.length > 0) {
        gsap.set(stackItems, {
          x: -200,
          rotate: -15,
          opacity: 0,
          scale: 0.88,
          force3D: true
        });
      }

      if (this.dossierPanel) {
        gsap.set(this.dossierPanel, {
          y: 60,
          rotate: 6,
          opacity: 0,
          force3D: true
        });
      }

      if (this.promptBar) {
        gsap.set(this.promptBar, { opacity: 0, y: 20, force3D: true });
      }
    }

    buildMasterTimeline() {
      if (typeof gsap === 'undefined') return;

      this.resetToInitialState();
      const stackItems = this.overlay.querySelectorAll('.p5r-cart-card, .p5r-cart-empty');

      // Build GSAP Master Entrance Timeline
      this.timeline = gsap.timeline({
        paused: true,
        onComplete: () => this.startIdlePhysics()
      });

      // 50ms: Dark gradient backdrop & Shadow Drift fade/expand in
      this.timeline.to(this.backdrop, {
        opacity: 1,
        duration: 0.2,
        ease: 'power2.out',
        force3D: true
      }, 0.05);

      if (this.shadowDrift) {
        this.timeline.to(this.shadowDrift, {
          opacity: 1,
          scale: 1,
          rotate: 0,
          duration: 0.55,
          ease: 'power3.out',
          force3D: true
        }, 0.05);
      }

      // 80ms: Left Red Filmstrip Reel Banner sweeps in from left
      if (this.filmstripBlock) {
        this.timeline.to(this.filmstripBlock, {
          xPercent: 0,
          duration: 0.45,
          ease: 'expo.out',
          force3D: true
        }, 0.08);
      }

      // 120ms: Top P5R Title Header slides & rotates in
      if (this.headerTitle) {
        this.timeline.to(this.headerTitle, {
          opacity: 1,
          x: 0,
          rotate: -3,
          duration: 0.38,
          ease: 'back.out(2)',
          force3D: true
        }, 0.12);
      }

      // 150ms: Layer 3 Comic Watermark Stamps scale & fade in
      if (this.graphicStamps.length > 0) {
        this.timeline.to(this.graphicStamps, {
          opacity: 0.035,
          scale: 1,
          duration: 0.45,
          stagger: 0.08,
          ease: 'power4.out',
          force3D: true
        }, 0.15);
      }

      // 250ms: Asymmetrical Save Slot Item Cards / Empty Card fly in with anticipation & overshoot curve
      if (stackItems.length > 0) {
        this.timeline.to(stackItems, {
          x: 0,
          rotate: (i, target) => {
            if (target.classList.contains('p5r-cart-empty')) return -2;
            return parseFloat(target.style.getPropertyValue('--card-rot')) || 1.5;
          },
          scale: 1,
          opacity: 1,
          duration: 0.48,
          stagger: 0.055,
          ease: 'back.out(2.2)',
          force3D: true
        }, 0.25);
      }

      // 280ms: Close Button drops in from top-right with elastic overshoot
      if (this.closeBtn) {
        this.timeline.to(this.closeBtn, {
          opacity: 1,
          y: 0,
          rotate: 0,
          duration: 0.35,
          ease: 'back.out(1.7)',
          force3D: true
        }, 0.28);
      }

      // 450ms: Right Checkout Dossier panel fades upward with rotational snap
      if (this.dossierPanel) {
        this.timeline.to(this.dossierPanel, {
          y: 0,
          rotate: 1,
          opacity: 1,
          duration: 0.38,
          ease: 'back.out(1.8)',
          force3D: true
        }, 0.45);
      }

      // 550ms: Footer prompt bar fades in
      if (this.promptBar) {
        this.timeline.to(this.promptBar, {
          opacity: 1,
          y: 0,
          duration: 0.3,
          ease: 'power3.out',
          force3D: true
        }, 0.55);
      }
    }

    startIdlePhysics() {
      if (typeof gsap === 'undefined' || this.idleTimeline) return;

      this.idleTimeline = gsap.timeline({ repeat: -1, yoyo: true });

      if (this.headerTitle) {
        this.idleTimeline.to(this.headerTitle, {
          y: '+=6',
          rotate: '+=0.8',
          duration: 4.0,
          ease: 'sine.inOut'
        }, 0);
      }
    }

    stopIdlePhysics() {
      if (this.idleTimeline) {
        this.idleTimeline.kill();
        this.idleTimeline = null;
      }
      if (this.headerTitle) {
        gsap.set(this.headerTitle, { y: 0, rotate: 0 });
      }
    }

    play() {
      if (typeof gsap === 'undefined') return;
      this.stopIdlePhysics();
      this.buildMasterTimeline();
      if (this.timeline) {
        this.timeline.restart();
      }
    }

    exit(onComplete) {
      this.stopIdlePhysics();
      if (typeof gsap === 'undefined') {
        if (onComplete) onComplete();
        return;
      }

      if (this.timeline) {
        this.timeline.pause();
      }

      const stackItems = this.overlay.querySelectorAll('.p5r-cart-card, .p5r-cart-empty');

      // High-Velocity P5R Exit Motion Timeline
      const exitTl = gsap.timeline({
        onComplete: () => {
          if (onComplete) onComplete();
        }
      });

      if (this.closeBtn) {
        exitTl.to(this.closeBtn, {
          opacity: 0,
          y: -30,
          rotate: 8,
          duration: 0.22,
          ease: 'power3.in',
          force3D: true
        }, 0);
      }

      if (this.graphicStamps.length > 0) {
        exitTl.to(this.graphicStamps, {
          opacity: 0,
          scale: 1.25,
          duration: 0.2,
          stagger: 0.04,
          ease: 'power2.in',
          force3D: true
        }, 0);
      }

      if (stackItems.length > 0) {
        exitTl.to(stackItems, {
          x: -240,
          rotate: -14,
          scale: 0.88,
          opacity: 0,
          duration: 0.3,
          ease: 'power3.in',
          stagger: 0.04,
          force3D: true
        }, 0);
      }

      if (this.dossierPanel) {
        exitTl.to(this.dossierPanel, {
          y: 65,
          rotate: 5,
          opacity: 0,
          duration: 0.28,
          ease: 'power3.in',
          force3D: true
        }, 0.05);
      }

      if (this.headerTitle) {
        exitTl.to(this.headerTitle, {
          x: -50,
          opacity: 0,
          duration: 0.25,
          ease: 'power2.in',
          force3D: true
        }, 0.08);
      }

      if (this.shadowDrift) {
        exitTl.to(this.shadowDrift, {
          opacity: 0,
          scale: 0.7,
          rotate: 8,
          duration: 0.22,
          ease: 'power2.in',
          force3D: true
        }, 0.1);
      }

      if (this.filmstripBlock) {
        exitTl.to(this.filmstripBlock, {
          xPercent: -120,
          duration: 0.35,
          ease: 'expo.in',
          force3D: true
        }, 0.1);
      }

      if (this.backdrop) {
        exitTl.to(this.backdrop, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
          force3D: true
        }, 0.15);
      }
    }

    initParallax() {
      if (this.parallaxBound || typeof gsap === 'undefined') return;
      const self = this;

      window.addEventListener('mousemove', (e) => {
        if (!self.overlay.classList.contains('is-active')) return;

        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        self.targetX = (e.clientX - cx) / cx;
        self.targetY = (e.clientY - cy) / cy;
      }, { passive: true });

      gsap.ticker.add(() => {
        if (!self.overlay.classList.contains('is-active')) return;

        self.mouseX += (self.targetX - self.mouseX) * 0.08;
        self.mouseY += (self.targetY - self.mouseY) * 0.08;

        const cards = self.overlay.querySelectorAll('.p5r-cart-card');
        const emptyCard = self.overlay.querySelector('.p5r-cart-empty');

        if (self.headerTitle) gsap.set(self.headerTitle, { x: self.mouseX * 16, y: self.mouseY * 10, rotate: self.mouseX * 2 });
        if (self.filmstripBlock) gsap.set(self.filmstripBlock, { x: self.mouseX * 8, y: self.mouseY * 4 });
        if (self.dossierPanel) gsap.set(self.dossierPanel, { x: self.mouseX * -14, y: self.mouseY * -10 });
        if (cards.length > 0) {
          cards.forEach((card, idx) => {
            const factor = (idx + 1) * 3;
            gsap.set(card, { x: self.mouseX * factor, y: self.mouseY * (factor * 0.5) });
          });
        }
        if (emptyCard) {
          gsap.set(emptyCard, {
            x: self.mouseX * 10,
            y: self.mouseY * 6,
            rotate: self.mouseX * 1.5,
            overwrite: 'auto'
          });
        }
      });

      this.parallaxBound = true;
    }

    bindCheckoutTransition() {
      // Instant redirect to checkout page on form submit
      var checkoutForm = this.overlay.querySelector('#p5rCartCheckoutForm');
      if (checkoutForm) {
        checkoutForm.addEventListener('submit', function () {
          // Direct browser navigation to checkout without delay
        });
      }
    }
  }

  global.Persona5CartMotion = Persona5CartMotion;
})(window);
